/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { WEB_BUTTON_FOCUS_RESET } from '../../shared/ui/webButtonFocusReset';
import type { BranchStockActivityRow } from '../../inventory/stock/public-api';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { formatQty } from '@shared/quantity';
import { StatusPill } from '../../shared/surface-cards/StatusPill'
import type { StatusPillTone } from '../../shared/surface-cards/StatusPill';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';

/** Ítems en preview embebido (si se usa tarjeta en canvas). */
export const BRANCH_ACTIVITY_PREVIEW_COUNT = 5;

export type BranchLike = { id: string; name: string };

type Props = {
  item: BranchStockActivityRow;
  branches: BranchLike[];
  /** Líneas para el detalle (descripción + cantidad/sucursal). */
  detailNumberOfLines?: number;
  onPress?: () => void;
};

const formatWhen = (iso: string) => formatLocalDateTime(iso);

/** Sufijo « · 1 u. · Sucursal X» alineado a la lógica previa de card/modal. */
export const branchActivityDetailSuffix = (
  item: BranchStockActivityRow,
  branches: BranchLike[]
): string | null => {
  const branchName = item.toBranchId
    ? branches.find((b) => b.id === item.toBranchId)?.name
    : item.fromBranchId
      ? branches.find((b) => b.id === item.fromBranchId)?.name
      : null;
  if (item.type === 'movement' && item.delta != null) {
    const sign = item.delta > 0 ? '+' : '';
    return `${sign}${formatQty(item.delta, item.unitOfMeasure)}`;
  }
  if (item.type === 'transfer' && item.quantity != null) {
    return `${formatQty(item.quantity, item.unitOfMeasure)}${branchName ? ` · ${branchName}` : ''}`;
  }
  return null;
};

function transferStatusChip(status: string | undefined): { label: string; tone: StatusPillTone } | null {
  if (!status) return null;
  switch (status) {
    case 'sent':
      return { label: 'Pendiente', tone: 'warning' };
    case 'completed':
      return { label: 'Completada', tone: 'service' };
    case 'received':
      return { label: 'Recibida', tone: 'success' };
    case 'rejected':
      return { label: 'Rechazada', tone: 'danger' };
    default:
      return null;
  }
}

export const BranchActivityRowView = ({ item, branches, detailNumberOfLines = 2, onPress }: Props) => {
  const detail = branchActivityDetailSuffix(item, branches);
  const chip = item.type === 'transfer' ? transferStatusChip(item.status) : null;

  const content = (
    <>
      <View style={styles.iconWrap}>
        <Ionicons
          name={item.type === 'transfer' ? 'swap-horizontal-outline' : 'cube-outline'}
          size={18}
          color={ticketColors.ink2}
        />
      </View>
      <View style={styles.body}>
        <View style={styles.topLine}>
          <Text style={posUi.checkoutHint} numberOfLines={1}>
            {formatWhen(item.date)}
          </Text>
          {chip ? <StatusPill label={chip.label} tone={chip.tone} compact /> : null}
        </View>
        <Text style={styles.productName} numberOfLines={1} ellipsizeMode="tail">
          {item.productName}
        </Text>
        {item.variantLabel ? (
          <Text style={[posUi.checkoutHint, { fontStyle: 'italic' }]} numberOfLines={1}>
            Variante: {item.variantLabel}
          </Text>
        ) : null}
        <Text style={posUi.checkoutHint} numberOfLines={detailNumberOfLines}>
          {item.description}
          {detail ? ` · ${detail}` : ''}
        </Text>
      </View>
      {onPress ? (
        <Ionicons name="chevron-forward" size={16} color={ticketColors.ink3} style={styles.rowChevron} />
      ) : null}
    </>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        style={[styles.row, styles.rowPressable, WEB_BUTTON_FOCUS_RESET]}
        onPress={onPress}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={`${item.productName}. ${item.description}`}
      >
        {content}
      </TouchableOpacity>
    );
  }

  return <View style={styles.row}>{content}</View>;
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
  },
  rowPressable: {
    paddingHorizontal: spacing.xs,
    marginHorizontal: -spacing.xs,
    borderRadius: 10,
  },
  rowChevron: {
    marginTop: 4,
    flexShrink: 0,
  },
  iconWrap: {
    paddingTop: 2,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  topLine: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  productName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
});
